import { characters } from '../data/characters.js';
import { bosses } from '../data/bosses.js';
import { baseMoves, unlockableMoves } from '../data/moves.js';
import { items as itemDefs, startingItems } from '../data/items.js';
import { storyNodes, START_NODE_ID } from '../data/story.js';
import { playerAttack, bossAttack, shuffleMoves } from './combatEngine.js';

export const SCREENS = {
  TITLE: 'TITLE',
  CHARACTER_SELECT: 'CHARACTER_SELECT',
  STORY: 'STORY',
  BATTLE: 'BATTLE',
  CODEX: 'CODEX',
};

function buildInventory(characterId) {
  const itemIds = startingItems[characterId] || [];
  const inv = [];
  for (const id of itemIds) {
    const existing = inv.find(i => i.id === id);
    if (existing) {
      existing.count += 1;
    } else {
      const def = itemDefs.find(i => i.id === id);
      if (def) {
        inv.push({ ...def, count: 1 });
      }
    }
  }
  return inv;
}

function buildPlayerFromCharacter(char) {
  return {
    characterId: char.id,
    name: char.name,
    emoji: char.emoji,
    hp: char.hp,
    maxHp: char.maxHp,
    atk: char.atk,
    def: char.def,
    luck: char.luck,
    level: 1,
    xp: 0,
    xpToNext: 100,
    passive: { ...char.passive },
    passiveStacks: 0,
    moves: [...baseMoves, ...unlockableMoves.filter(m => m.unlockLevel <= 3)],
    inventory: buildInventory(char.id),
    buffs: [],
    debuffs: [],
    dots: [],
    skipNextTurn: false,
    healDisabled: false,
    healDisabledTurns: 0,
    nextAttackDouble: false,
    movesShuffled: false,
    loot: [],
    color: char.color,
  };
}

export function createInitialState() {
  return {
    screen: SCREENS.TITLE,
    player: null,
    boss: null,
    storyNodeId: null,
    visitedNodes: [],
    combatReturnNode: null,
    lastPlayerMoveType: null,
    battleLog: [],
    turn: 'player',
    turnNumber: 1,
    isAnimating: false,
    previousScreen: SCREENS.TITLE,
  };
}

export const ACTION_TYPES = {
  // Core
  START_GAME: 'START_GAME',
  SELECT_CHARACTER: 'SELECT_CHARACTER',
  BACK_TO_TITLE: 'BACK_TO_TITLE',
  ANIMATION_DONE: 'ANIMATION_DONE',
  // Story
  STORY_GO_TO: 'STORY_GO_TO',
  STORY_CHOICE: 'STORY_CHOICE',
  STORY_TO_COMBAT: 'STORY_TO_COMBAT',
  // Combat
  PLAYER_MOVE: 'PLAYER_MOVE',
  PLAYER_USE_ITEM: 'PLAYER_USE_ITEM',
  BOSS_TURN: 'BOSS_TURN',
  // Codex
  OPEN_CODEX: 'OPEN_CODEX',
  CLOSE_CODEX: 'CLOSE_CODEX',
};

// ---------- helpers ----------

function recordVisit(state, nodeId) {
  if (!nodeId) return state;
  if (state.visitedNodes.includes(nodeId)) return state;
  return { ...state, visitedNodes: [...state.visitedNodes, nodeId] };
}

function goToStoryNode(state, nodeId) {
  if (!nodeId) return state;
  const next = recordVisit(state, nodeId);
  return {
    ...next,
    screen: SCREENS.STORY,
    storyNodeId: nodeId,
    combatReturnNode: null,
    lastPlayerMoveType: null,
  };
}

/**
 * After a combat tick, check if combat just ended and we have a story
 * return target. If so, navigate to the appropriate story node.
 */
function maybeFinalizeCombat(state) {
  // Boss defeated → onWin
  if (state.boss && state.boss.hp <= 0 && state.combatReturnNode) {
    return goToStoryNode(state, state.combatReturnNode.onWin);
  }
  // Player defeated → onLose
  if (state.player && state.player.hp <= 0 && state.combatReturnNode) {
    return goToStoryNode(state, state.combatReturnNode.onLose);
  }
  return state;
}

// ---------- reducer ----------

export function gameReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.START_GAME:
      return {
        ...createInitialState(),
        screen: SCREENS.CHARACTER_SELECT,
      };

    case ACTION_TYPES.SELECT_CHARACTER: {
      const char = characters.find(c => c.id === action.characterId);
      if (!char) return state;
      const player = buildPlayerFromCharacter(char);
      const fresh = {
        ...createInitialState(),
        player,
      };
      return goToStoryNode(fresh, START_NODE_ID);
    }

    case ACTION_TYPES.STORY_GO_TO: {
      return goToStoryNode(state, action.nodeId);
    }

    case ACTION_TYPES.STORY_CHOICE: {
      const node = storyNodes[state.storyNodeId];
      if (!node || node.type !== 'choice') return state;
      const choice = node.choices?.[action.choiceIndex];
      if (!choice) return state;
      return goToStoryNode(state, choice.next);
    }

    case ACTION_TYPES.STORY_TO_COMBAT: {
      const node = storyNodes[state.storyNodeId];
      if (!node || node.type !== 'combat' || !node.combat) return state;
      const bossDef = bosses.find(b => b.id === node.combat.bossId);
      if (!bossDef) return state;
      // Reset player combat-only state, restore HP
      const player = {
        ...state.player,
        hp: state.player.maxHp,
        buffs: [],
        debuffs: [],
        dots: [],
        skipNextTurn: false,
        healDisabled: false,
        healDisabledTurns: 0,
        nextAttackDouble: false,
        movesShuffled: false,
        passiveStacks: 0,
      };
      return {
        ...state,
        screen: SCREENS.BATTLE,
        player,
        boss: { ...bossDef },
        battleLog: [
          { text: `${bossDef.emoji} ${bossDef.name} se dresse devant toi !`, type: 'system' },
          { text: `"${bossDef.introDiag}"`, type: 'boss' },
        ],
        turn: 'player',
        turnNumber: 1,
        isAnimating: false,
        combatReturnNode: { ...node.combat },
        lastPlayerMoveType: null,
      };
    }

    case ACTION_TYPES.PLAYER_MOVE: {
      if (state.turn !== 'player' || state.isAnimating) return state;
      const move = action.move;
      const trackedState = { ...state, lastPlayerMoveType: move.type };
      let next = playerAttack(trackedState, move);
      if (next.player?.movesShuffled) {
        next = { ...next, player: { ...next.player, movesShuffled: false } };
      }
      return maybeFinalizeCombat(next);
    }

    case ACTION_TYPES.PLAYER_USE_ITEM: {
      if (state.turn !== 'player' || state.isAnimating) return state;
      const itemId = action.itemId;
      const player = { ...state.player };
      const log = [...state.battleLog];
      const inventory = [...(player.inventory || [])];

      const itemIndex = inventory.findIndex(i => i.id === itemId && i.count > 0);
      if (itemIndex === -1) return state;

      const item = { ...inventory[itemIndex] };
      item.count -= 1;
      if (item.count <= 0) {
        inventory.splice(itemIndex, 1);
      } else {
        inventory[itemIndex] = item;
      }

      switch (itemId) {
        case 'cafe': {
          if (player.healDisabled) {
            log.push({ text: "Soins bloqués ! Même le café ne peut rien pour toi.", type: 'system' });
            break;
          }
          const old = player.hp;
          player.hp = Math.min(player.maxHp, player.hp + 15);
          log.push({ text: `☕ Café de la machine — +${player.hp - old} HP. Goût douteux, efficacité prouvée.`, type: 'player' });
          break;
        }
        case 'photocopie':
          player.buffs = [...(player.buffs || []), { stat: 'atk', value: 5, turnsLeft: 3 }];
          player.atk += 5;
          log.push({ text: "📄 Photocopie certifiée conforme — +5 ATK pendant 3 tours !", type: 'player' });
          break;
        case 'ticket':
          player.buffs = [...(player.buffs || []), { stat: 'def', value: 8, turnsLeft: 3 }];
          player.def += 8;
          log.push({ text: "🎫 Ticket de file d'attente — +8 DEF pendant 3 tours !", type: 'player' });
          break;
        case 'justificatif': {
          if (player.healDisabled) {
            log.push({ text: "Soins bloqués ! Ton justificatif ne sert à rien.", type: 'system' });
            break;
          }
          const old = player.hp;
          player.hp = Math.min(player.maxHp, player.hp + 25);
          log.push({ text: `🏠 Justificatif de domicile — +${player.hp - old} HP. La preuve que tu existes.`, type: 'player' });
          break;
        }
        case 'rib':
          player.buffs = [...(player.buffs || []), { stat: 'luck', value: 10, turnsLeft: 3 }];
          player.luck += 10;
          log.push({ text: "🏦 RIB annoté au crayon — +10 LUCK pendant 3 tours !", type: 'player' });
          break;
        case 'attestation':
          player.hp = Math.min(player.maxHp, player.hp + 30);
          log.push({ text: "✋ Attestation sur l'honneur — +30 HP ! Tu jures que c'est vrai.", type: 'player' });
          break;
        default:
          break;
      }

      player.inventory = inventory;
      return {
        ...state,
        player,
        battleLog: log,
        turn: 'boss',
        isAnimating: true,
        lastPlayerMoveType: 'item',
      };
    }

    case ACTION_TYPES.BOSS_TURN: {
      if (state.turn !== 'boss') return state;
      let next = bossAttack(state);
      if (next.turn === 'player') {
        next = { ...next, turnNumber: (state.turnNumber || 1) + 1 };
      }
      return maybeFinalizeCombat(next);
    }

    case ACTION_TYPES.OPEN_CODEX:
      return { ...state, previousScreen: state.screen, screen: SCREENS.CODEX };

    case ACTION_TYPES.CLOSE_CODEX:
      return { ...state, screen: state.previousScreen || SCREENS.TITLE };

    case ACTION_TYPES.BACK_TO_TITLE:
      return createInitialState();

    case ACTION_TYPES.ANIMATION_DONE:
      return { ...state, isAnimating: false };

    default:
      return state;
  }
}
